import { addYears } from 'date-fns/addYears'
import { isWithinInterval } from 'date-fns/isWithinInterval'
import { ChangeEvent, KeyboardEvent, ReactNode, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { REGEXPS } from 'aem-libs-react-ts/src/constants'
import { useInputGaTracking } from 'aem-libs-react-ts/src/hooks/useInputGaTracking'
import { useOutsideClick } from 'aem-libs-react-ts/src/hooks/useOutsideClick'
import { inputId } from 'aem-libs-tests/src/constants/inputId'
import { createTestId } from 'aem-libs-tests/src/utility/createTestId'
import { Maybe } from 'aem-libs-ts/src/types/utility'
import { isDefined } from 'aem-libs-ts/src/utils/common/isDefined'
import { pipe } from 'aem-libs-ts/src/utils/common/pipe'
import { formatShortDate } from 'aem-libs-ts/src/utils/formatting/dates/formatShortDate'

import { CommonInputWrapper } from '../CommonInputWrapper'
import { Datepicker, DatepickerProps } from '../datepicker'
import { CommonInputProps } from '../types'
import { useFieldError } from '../useFieldError'
import { useFieldMeta } from '../useFieldMeta'
import { ValidationMessage } from '../validation-message'
import * as DateFieldStyled from './DateField.styled'
import { parseDateProcess } from './helpers'
import cs from './i18n/cs.json'
import en from './i18n/en.json'


interface BaseDateFieldProps
  extends Omit<DatepickerProps, 'value'>,
    Omit<CommonInputProps, 'label' | 'onChange' | 'value' | 'enableNull'> {
  disabled?: boolean
  name?: string
  id?: string
  label?: string | ReactNode
  fallbackDate?: Date
  onYearsChange?: (value: number) => void
}

interface RequiredDateProps {
  inputMode?: 'default'
  value: Date
  onChange: (date: Date) => void
  enableNullValue?: false
}

interface OptionalDateProps {
  inputMode: 'nullable'
  value: Maybe<Date>
  onChange: (date: Date | null) => void
  enableNullValue: true
}

type ValueProps = RequiredDateProps | OptionalDateProps
export type DateFieldProps = BaseDateFieldProps & ValueProps

export const DateField = ({
  fallbackDate = new Date(),
  disableDates,
  onChange,
  label,
  validationMessage,
  hideValidation,
  disabled,
  name,
  value,
  yearsRange,
  mode,
  width,
  tip,
  responsive,
  testId,
  onYearsChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  inputMode = 'default',
  enableNullValue,
  ...dateFieldProps
}: DateFieldProps) => {
  const { t, i18n } = useTranslation('date')
  i18n.addResourceBundle('cs', 'date', cs)
  i18n.addResourceBundle('en', 'date', en)

  const formattedValue = value ? formatShortDate(value) : ''
  const [fieldValue, setFieldValue] = useState(formattedValue ?? '')
  const [currentDate, setCurrentDate] = useState<Maybe<Date>>(value)
  const [datePickerVisible, setDatePickerVisible] = useState(false)
  const datepickerRef = useOutsideClick<HTMLDivElement>({
    onClick: () => setDatePickerVisible(false)
  })
  const datepickerContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (formattedValue !== fieldValue) {
      setFieldValue(formattedValue)
      setCurrentDate(value)
    }
  }, [value])

  const { trackInputChange } = useInputGaTracking({
    enableGaTracking: true,
    description: dateFieldProps.trackingDescription,
    id: dateFieldProps.id
  })

  const handleDatepickerChange = (v: Date, keepVisible?: boolean) => {
    const dateValue = formatShortDate(v)
    setCurrentDate(v)
    setFieldValue(dateValue)
    trackInputChange(dateValue, 'editbox')
    if (onChange) {
      onChange(v)
    }
    if (!keepVisible) {
      setDatePickerVisible(false)
    }
  }

  const checkDisabledDates =
    (fallbackDate: Date) =>
    (date: Date): Date => {
      if (disableDates && disableDates(date)) {
        return fallbackDate
      }

      return date
    }

  const intervalCheck =
    (fallbackDate: Date) =>
    (date: Date): Date => {
      return isWithinInterval(date, {
        start: addYears(new Date(), -100),
        end: addYears(new Date(), 100)
      })
        ? date
        : fallbackDate
    }

  const handleDateFinalizeChange = (v: string) => {
    const checkedDate = currentDate as Date
    trackInputChange(v, 'editbox')
    const newDateValue = pipe(() => v.replace(/\s/g, ''))
      .throwWhen(
        (v) => v.length === 0,
        () => (enableNullValue ? null : fallbackDate)
      )
      .map(parseDateProcess(checkedDate))
      .map(checkDisabledDates(checkedDate))
      .map(intervalCheck(checkedDate))
      .getOrElse((d) => d)

    setCurrentDate(newDateValue)
    setFieldValue(isDefined(newDateValue) ? formatShortDate(newDateValue as Date) : '')
    if (onChange) {
      onChange(newDateValue as Date)
    }
  }

  const handleBlur = () => {
    handleDateFinalizeChange(fieldValue)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleDateFinalizeChange(fieldValue)
      setDatePickerVisible(false)
    }
  }

  const handleShowDatepicker = () => {
    if (!disabled) {
      setDatePickerVisible(true)
    }
  }

  const handleDatepickerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab' || !datepickerContainerRef.current) {
      return
    }

    const focusable = Array.from(
      datepickerContainerRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1)

    if (!focusable.length) {
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (focusable.length === 1) {
      e.preventDefault()
      first?.focus()
      return
    }

    if (e.shiftKey) {
      if (document.activeElement === first || !datepickerContainerRef.current.contains(document.activeElement)) {
        last?.focus()
        e.preventDefault()
      }
    } else {
      if (document.activeElement === last || !datepickerContainerRef.current.contains(document.activeElement)) {
        first?.focus()
        e.preventDefault()
      }
    }
  }

  const { showError, ariaErrorProps, errorMessage } = useFieldError(name, validationMessage, { hideValidation })
  const fieldProps = useFieldMeta(name, { tip: undefined }, ariaErrorProps)

  const usedTestId = testId ?? 'date-field'

  return (
    <DateFieldStyled.Container $valueWidth={width} $responsive={responsive ?? false}>
      <CommonInputWrapper id={dateFieldProps.id} label={label} tip={tip} testId={usedTestId}>
        <DateFieldStyled.InputContainer onClick={handleShowDatepicker} isActive={datePickerVisible}>
          <DateFieldStyled.Input
            id={dateFieldProps.id}
            name={name}
            type={'text'}
            disabled={disabled}
            value={fieldValue}
            data-testid={createTestId(usedTestId, inputId.input)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              if (REGEXPS.DATE.test(e.currentTarget.value)) {
                setFieldValue(e.target.value)
              }
            }}
            {...fieldProps}
          />
          <DateFieldStyled.CalendarIco
            aria-label={t('CALENDAR_OPEN_BUTTON')}
            type={'button'}
            tabIndex={0}
            id={'calendar-ico'}
            onChange={() => setDatePickerVisible((prev) => !prev)}
          />
        </DateFieldStyled.InputContainer>
        {showError && <ValidationMessage id={name} message={errorMessage} testId={usedTestId} />}
      </CommonInputWrapper>
      <DateFieldStyled.DatepickerWrapper ref={datepickerRef}>
        {datePickerVisible && (
          <DateFieldStyled.DatepickerContainer ref={datepickerContainerRef} onKeyDown={handleDatepickerKeyDown}>
            <Datepicker
              onClose={() => setDatePickerVisible(false)}
              testId={`${usedTestId}-dp`}
              mode={mode}
              disableDates={disableDates}
              value={currentDate ?? value ?? fallbackDate}
              onChange={handleDatepickerChange}
              yearsRange={yearsRange}
              onYearsChange={onYearsChange}
            />
          </DateFieldStyled.DatepickerContainer>
        )}
      </DateFieldStyled.DatepickerWrapper>
    </DateFieldStyled.Container>
  )
}
